import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Bot, MessagesSquare, Settings2, Users2 } from 'lucide-react';
import SEO from '../components/SEO';
import { Section } from '../components/Section';
import { buildFAQSchema, GeoSeoSections } from '../components/GeoSeoSections';

const faqs = [
  {
    question: '¿Qué tipo de automatización con IA puede aplicar una empresa en Puerto Rico?',
    answer: 'Seguimiento comercial, respuestas iniciales, organización de leads, coordinación interna, automatización documental y reporting operativo.'
  },
  {
    question: '¿Esto sustituye al equipo humano?',
    answer: 'No. La idea es reducir fricción, tiempos muertos y tareas repetitivas para que el equipo humano se centre en cierre, atención y dirección.'
  },
  {
    question: '¿Sirve para negocios con WhatsApp, formularios y llamadas?',
    answer: 'Sí. Justo ahí suele haber más fuga operativa: mensajes sin seguimiento, leads no priorizados y tareas dispersas entre varios canales.'
  },
  {
    question: '¿Qué sectores pueden beneficiarse más en Puerto Rico?',
    answer: 'Servicios profesionales, clínicas, formación, inmobiliaria, despachos y empresas comerciales que necesitan más orden y respuesta rápida.'
  }
];

const successCases = [
  {
    title: 'Empresa de servicios con leads entrando por varios canales',
    copy: 'Se estructura el flujo entre formulario, WhatsApp, llamada y seguimiento para que cada oportunidad tenga siguiente acción y prioridad visibles.',
    outcome: 'menos leads olvidados y mejor respuesta'
  },
  {
    title: 'Negocio comercial con tareas repetitivas y poca trazabilidad',
    copy: 'Automatización entre CRM, mensajes y tareas para eliminar dobles trabajos y dejar historial claro de lo que ya se hizo.',
    outcome: 'menos caos operativo y más control'
  },
  {
    title: 'Equipo pequeño que necesita parecer más grande',
    copy: 'Capas de agentes y automatizaciones para sostener atención, seguimiento y reporting sin ampliar estructura demasiado pronto.',
    outcome: 'más capacidad percibida y más consistencia'
  }
];

const sectors = [
  { badge: 'Puerto Rico', title: 'Servicios locales premium', copy: 'Perfecto para negocios que viven del seguimiento rápido y la atención bien coordinada.' },
  { badge: 'Puerto Rico', title: 'Clínicas y salud', copy: 'Ayuda a ordenar pacientes potenciales, citas, documentación y comunicación operativa.' },
  { badge: 'Puerto Rico', title: 'Educación y asesoría', copy: 'Muy útil cuando hay leads interesados, pagos, entregables y seguimiento continuo.' },
  { badge: 'Puerto Rico', title: 'Equipos comerciales', copy: 'Encaja en estructuras que quieren responder más rápido sin perder control de pipeline.' }
];

const localSignals = [
  'Pensado para empresas en Puerto Rico que necesitan conectar mensajería, seguimiento y operación sin añadir más complejidad.',
  'Muy útil cuando la empresa depende de respuestas rápidas, llamadas, formularios web y coordinación entre varias personas.',
  'La propuesta combina automatización con IA, agentes conversacionales y criterio operativo para ventas y servicio.',
  'Se adapta a fases de crecimiento donde el reto no es solo captar más, sino no perder oportunidades por desorden interno.'
];

const faqSchema = buildFAQSchema(faqs);

export default function AutomatizacionPuertoRicoPage() {
  return (
    <>
      <SEO
        title="Automatización con IA para empresas en Puerto Rico | Neuralia369"
        description="Automatización con IA para empresas en Puerto Rico: seguimiento comercial, agentes, omnicanalidad y operaciones con Neuralia369."
        path="/automatizacion-ia-puerto-rico"
        keywords="automatización con IA Puerto Rico, agentes IA Puerto Rico, marketing IA Puerto Rico, seguimiento comercial IA"
        schemas={[faqSchema]}
      />
      <div className="min-h-screen bg-background pt-24 pb-20" data-testid="automatizacion-puerto-rico-page">
        <Section>
          <div className="max-w-6xl mx-auto grid gap-10 xl:grid-cols-[1.03fr_0.97fr]">
            <div>
              <p className="editorial-kicker mb-4">Puerto Rico · Neuralia369</p>
              <h1>Automatización con IA para empresas en <span className="gradient-text">Puerto Rico</span></h1>
              <p className="mt-6 text-lg text-text-body">Conectamos mensajes, tareas, seguimiento de leads, documentos y respuesta operativa para que tu empresa avance con menos fricción y más control.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                {['Puerto Rico', 'Automatización', 'Agentes IA', 'Seguimiento comercial'].map((item) => <span key={item} className="insight-chip">{item}</span>)}
              </div>
              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/contacto" className="btn-primary" data-testid="automatizacion-puerto-rico-contact-cta">Hablar con Neuralia<ArrowRight className="w-5 h-5" /></Link>
                <Link to="/agenda" className="btn-secondary" data-testid="automatizacion-puerto-rico-agenda-cta">Reservar llamada</Link>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {[
                { icon: MessagesSquare, title: 'Mensajería y leads', copy: 'Respuesta más rápida y trazable entre canales.' },
                { icon: Settings2, title: 'Procesos', copy: 'Automatización entre formularios, CRM y tareas internas.' },
                { icon: Bot, title: 'Agentes IA', copy: 'Capas conversacionales para atención, seguimiento y reporting.' },
                { icon: Users2, title: 'Equipo', copy: 'Menos carga operativa manual y más consistencia.' },
              ].map(({ icon: Icon, title, copy }) => (
                <div key={title} className="glass-card tracing-panel p-6" data-testid={`automatizacion-pr-card-${title}`}>
                  <Icon className="w-6 h-6 text-primary" />
                  <p className="mt-4 text-lg font-semibold text-text-heading">{title}</p>
                  <p className="mt-3 text-sm text-text-body">{copy}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>
        <GeoSeoSections
          slug="automatizacion-puerto-rico"
          regionLabel="Puerto Rico"
          successCases={successCases}
          sectors={sectors}
          faqs={faqs}
          localSignals={localSignals}
        />
      </div>
    </>
  );
}